import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Trash2,
  Sparkles,
  PenTool,
  Loader2,
  Send,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import BrainstormCanvas from "@/components/BrainstormCanvas";

export default function IdeaDetail({
  idea,
  onBack,
  onUpdate,
  bits,
  onBitCreated,
}) {
  const [activeTab, setActiveTab] = useState(idea.last_tab || "brainstorm");
  const [rootPremise, setRootPremise] = useState(idea.root_premise);
  const [rootDraft, setRootDraft] = useState(idea.root_draft || "");
  const [angles, setAngles] = useState(idea.angles || []);
  const [goodLines, setGoodLines] = useState(idea.good_lines || []);
  const [selectedAngle, setSelectedAngle] = useState(null);
  const [draftContent, setDraftContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [promoteContent, setPromoteContent] = useState("");
  const [promoteTitle, setPromoteTitle] = useState("");
  const [promoteAction, setPromoteAction] = useState("new_bit");
  const [targetBitId, setTargetBitId] = useState("");
  
  // Canvas state
  const [canvasZoom, setCanvasZoom] = useState(idea.canvas_zoom || 1);
  const [canvasOffsetX, setCanvasOffsetX] = useState(idea.canvas_offset_x || 0);
  const [canvasOffsetY, setCanvasOffsetY] = useState(idea.canvas_offset_y || 0);

  const saveTimeoutRef = useRef(null);
  const anglesRef = useRef(angles);
  const rootPremiseRef = useRef(rootPremise);
  const rootDraftRef = useRef(rootDraft);
  const goodLinesRef = useRef(goodLines);
  const activeTabRef = useRef(activeTab);
  const selectedAngleRef = useRef(selectedAngle);
  const canvasZoomRef = useRef(canvasZoom);
  const canvasOffsetXRef = useRef(canvasOffsetX);
  const canvasOffsetYRef = useRef(canvasOffsetY);

  // Keep refs updated
  useEffect(() => { anglesRef.current = angles; }, [angles]);
  useEffect(() => { rootPremiseRef.current = rootPremise; }, [rootPremise]);
  useEffect(() => { rootDraftRef.current = rootDraft; }, [rootDraft]);
  useEffect(() => { goodLinesRef.current = goodLines; }, [goodLines]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { selectedAngleRef.current = selectedAngle; }, [selectedAngle]);
  useEffect(() => { canvasZoomRef.current = canvasZoom; }, [canvasZoom]);
  useEffect(() => { canvasOffsetXRef.current = canvasOffsetX; }, [canvasOffsetX]);
  useEffect(() => { canvasOffsetYRef.current = canvasOffsetY; }, [canvasOffsetY]);

  // Immediate save function (for critical moments)
  const saveNow = useCallback(async () => {
    try {
      await fetch(`${API}/play/ideas/${idea.idea_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          root_premise: rootPremiseRef.current,
          root_draft: rootDraftRef.current,
          angles: anglesRef.current,
          good_lines: goodLinesRef.current,
          last_tab: activeTabRef.current,
          last_angle_id: selectedAngleRef.current?.angle_id || null,
          canvas_zoom: canvasZoomRef.current,
          canvas_offset_x: canvasOffsetXRef.current,
          canvas_offset_y: canvasOffsetYRef.current,
        }),
      });
    } catch (error) {
      console.error("Save failed:", error);
    }
  }, [idea.idea_id]);

  // Auto-save on changes
  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API}/play/ideas/${idea.idea_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          root_premise: rootPremise,
          root_draft: rootDraft,
          angles: angles,
          good_lines: goodLines,
          last_tab: activeTab,
          last_angle_id: selectedAngle?.angle_id || null,
          canvas_zoom: canvasZoom,
          canvas_offset_x: canvasOffsetX,
          canvas_offset_y: canvasOffsetY,
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Save failed:", errorText);
        toast.error("Failed to save changes");
      }
    } catch (error) {
      console.error("Auto-save failed:", error);
      toast.error("Failed to save: " + error.message);
    } finally {
      setIsSaving(false);
    }
  }, [idea.idea_id, rootPremise, rootDraft, angles, goodLines, activeTab, selectedAngle, canvasZoom, canvasOffsetX, canvasOffsetY]);

  // Debounced auto-save
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(saveChanges, 500);
    return () => clearTimeout(saveTimeoutRef.current);
  }, [rootPremise, rootDraft, angles, goodLines, activeTab, selectedAngle, canvasZoom, canvasOffsetX, canvasOffsetY, saveChanges]);

  // Save on page visibility change and before unload
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveNow();
      }
    };
    
    const handleBeforeUnload = () => {
      saveNow();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Final save on component unmount
      saveNow();
    };
  }, [saveNow]);

  // Restore last selected angle
  useEffect(() => {
    if (idea.last_angle_id && angles.length > 0) {
      const lastAngle = angles.find((a) => a.angle_id === idea.last_angle_id);
      if (lastAngle) {
        setSelectedAngle(lastAngle);
        setDraftContent(lastAngle.draft || "");
      }
    }
  }, []);

  // ============ CANVAS HANDLERS ============

  const handleCanvasStateChange = useCallback(({ zoom, offsetX, offsetY }) => {
    setCanvasZoom(zoom);
    setCanvasOffsetX(offsetX);
    setCanvasOffsetY(offsetY);
  }, []);

  // ============ WORK THIS (send to Punch It Up) ============

  const workAngle = (angle) => {
    // Handle root node
    if (angle.angle_id === "root") {
      setSelectedAngle({ angle_id: "root", text: rootPremise, draft: rootDraft, parent_id: null });
      setDraftContent(rootDraft);
    } else {
      setSelectedAngle(angle);
      setDraftContent(angle.draft || "");
    }
    setActiveTab("punchitup");
  };

  // ============ DRAFT OPERATIONS ============

  const handleDraftChange = (content) => {
    setDraftContent(content);
    if (selectedAngle) {
      if (selectedAngle.angle_id === "root") {
        // Save draft for the root/main idea
        setRootDraft(content);
      } else {
        // Save draft for subtopics
        setAngles(
          angles.map((a) =>
            a.angle_id === selectedAngle.angle_id
              ? { ...a, draft: content, updated_at: new Date().toISOString() }
              : a
          )
        );
      }
    }
  };

  // ============ GOOD LINES ============

  const saveLine = () => {
    const selection = window.getSelection()?.toString();
    if (!selection?.trim()) {
      toast.error("Select some text first");
      return;
    }

    const newLine = {
      line_id: `line_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      text: selection.trim(),
      from_angle_id: selectedAngle?.angle_id || null,
      created_at: new Date().toISOString(),
    };

    setGoodLines([newLine, ...goodLines]);
    toast.success("Line saved!");
  };

  const deleteLine = (lineId) => {
    setGoodLines(goodLines.filter((l) => l.line_id !== lineId));
  };

  // ============ PROMOTE TO WRITE ============

  const openPromoteModal = (content, title = "") => {
    setPromoteContent(content);
    setPromoteTitle(title);
    setPromoteAction("new_bit");
    setTargetBitId("");
    setIsPromoteModalOpen(true);
  };

  const handlePromote = async () => {
    if (!promoteContent.trim()) return;

    try {
      const response = await fetch(`${API}/play/ideas/${idea.idea_id}/to-write`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          content: promoteContent,
          title: promoteTitle,
          action: promoteAction,
          target_bit_id: promoteAction === "add_to_bit" ? targetBitId : null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setIsPromoteModalOpen(false);
        onBitCreated?.(data.bit);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send to Write");
      }
    } catch (error) {
      toast.error("Failed to send to Write");
    }
  };

  // Handle promoting entire idea to Write
  const handlePromoteAll = async () => {
    try {
      // Save current state first
      await saveNow();
      
      const response = await fetch(`${API}/play/ideas/${idea.idea_id}/promote-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        onBitCreated?.(data.bit);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to move to Write");
      }
    } catch (error) {
      toast.error("Failed to move to Write: " + error.message);
    }
  };

  return (
    <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 100px)' }} data-testid="idea-detail">
      {/* Header - compact */}
      <div className="border-b border-border px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="gap-2"
            data-testid="back-to-play"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="h-4 w-px bg-border" />
          <span className="text-xs text-muted-foreground">
            {isSaving ? (
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </span>
            ) : (
              "Saved"
            )}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="border-b border-border px-4 flex items-center justify-between">
            <TabsList className="h-9 bg-transparent">
              <TabsTrigger
                value="brainstorm"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-2"
                data-testid="tab-brainstorm"
              >
                <Sparkles className="w-4 h-4" />
                Brainstorm
              </TabsTrigger>
              <TabsTrigger
                value="punchitup"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-2"
                data-testid="tab-punchitup"
              >
                <PenTool className="w-4 h-4" />
                Punch It Up
              </TabsTrigger>
            </TabsList>
            
            <Button
              size="sm"
              variant="outline"
              onClick={handlePromoteAll}
              className="text-xs bg-primary/10 hover:bg-primary/20 border-primary/30"
              data-testid="promote-all-btn"
              title="Move entire brainstorm to Write"
            >
              <Send className="w-3 h-3 mr-1" />
              Move All to Write
            </Button>
          </div>

          {/* ============ BRAINSTORM TAB (Canvas) ============ */}
          <TabsContent
            value="brainstorm"
            className="flex-1 overflow-hidden mt-0"
          >
            <BrainstormCanvas
              rootPremise={rootPremise}
              onRootPremiseChange={setRootPremise}
              angles={angles}
              onAnglesChange={setAngles}
              onWorkThis={workAngle}
              onUseInWrite={(node) => openPromoteModal(node.text, node.text.slice(0, 50))}
              canvasZoom={canvasZoom}
              canvasOffsetX={canvasOffsetX}
              canvasOffsetY={canvasOffsetY}
              onCanvasStateChange={handleCanvasStateChange}
            />
          </TabsContent>

          {/* ============ PUNCH IT UP TAB ============ */}
          <TabsContent
            value="punchitup"
            className="flex-1 overflow-hidden mt-0"
          >
            <div className="h-full flex min-h-0">
              {/* Left: Angle List */}
              <div className="w-56 border-r border-border flex flex-col shrink-0">
                <div className="px-3 py-2 border-b border-border shrink-0">
                  <h4 className="font-semibold text-sm">Pick an Angle</h4>
                </div>
                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-2 space-y-1">
                    {/* Root premise as first option */}
                    <button
                      className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                        selectedAngle?.angle_id === "root"
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted/50"
                      }`}
                      onClick={() => {
                        setSelectedAngle({ angle_id: "root", text: rootPremise, draft: rootDraft, parent_id: null });
                        setDraftContent(rootDraft);
                      }}
                      data-testid="select-root-premise"
                    >
                      <span className="line-clamp-2 font-medium">{rootPremise}</span>
                      <span className="text-xs opacity-70 block mt-1">
                        Main Idea{rootDraft ? " • Has draft" : ""}
                      </span>
                    </button>
                    
                    {angles.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">
                        No angles yet. Create some in Brainstorm first.
                      </p>
                    ) : (
                      angles.map((angle) => (
                        <button
                          key={angle.angle_id}
                          className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                            selectedAngle?.angle_id === angle.angle_id
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-muted/50"
                          }`}
                          onClick={() => {
                            setSelectedAngle(angle);
                            setDraftContent(angle.draft || "");
                          }}
                          data-testid={`select-angle-${angle.angle_id}`}
                        >
                          <span className="line-clamp-2">{angle.text}</span>
                          {angle.draft && (
                            <span className="text-xs opacity-70 block mt-1">
                              Has draft
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>

              {/* Right: Editor */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between shrink-0">
                  <h4 className="font-semibold text-sm">Punch It Up</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={saveLine}
                      className="text-xs"
                      data-testid="save-line-btn"
                    >
                      <Star className="w-3 h-3 mr-1" />
                      Save Line
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (!draftContent.trim()) {
                          toast.error("Write something first");
                          return;
                        }
                        openPromoteModal(draftContent, selectedAngle?.text || "");
                      }}
                      className="text-xs"
                      disabled={!draftContent.trim()}
                      data-testid="move-to-write-btn"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Move to Write
                    </Button>
                  </div>
                </div>

                <div className="flex-1 p-3 min-h-0">
                  {selectedAngle ? (
                    <div className="h-full flex flex-col min-h-0">
                      <div className="mb-2 p-2 bg-muted/30 rounded-sm shrink-0">
                        <span className="text-xs text-muted-foreground">Working on:</span>
                        <p className="text-sm font-medium">{selectedAngle.text}</p>
                      </div>
                      <Textarea
                        value={draftContent}
                        onChange={(e) => handleDraftChange(e.target.value)}
                        placeholder="Rant, riff, test angles, try tags…"
                        className="flex-1 font-mono text-sm resize-none min-h-0"
                        data-testid="draft-textarea"
                      />
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-center text-muted-foreground">
                      <div>
                        <PenTool className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">Select an angle to work on</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Good Lines Tray */}
                {goodLines.length > 0 && (
                  <div className="border-t border-border p-2 shrink-0">
                    <div className="flex items-center justify-between mb-1">
                      <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Good Lines
                      </h5>
                      <span className="text-xs text-muted-foreground">
                        Stuff you don&apos;t want to lose
                      </span>
                    </div>
                    <ScrollArea className="max-h-24">
                      <div className="space-y-1">
                        {goodLines.map((line) => (
                          <div
                            key={line.line_id}
                            className="flex items-start gap-2 p-2 bg-muted/30 rounded-sm group"
                          >
                            <Star className="w-3 h-3 text-primary shrink-0 mt-1" />
                            <p className="flex-1 text-sm font-mono">{line.text}</p>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs"
                                onClick={() => openPromoteModal(line.text, "")}
                                data-testid={`use-line-${line.line_id}`}
                              >
                                Use in Write
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                onClick={() => deleteLine(line.line_id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Promote to Write Modal */}
      <Dialog open={isPromoteModalOpen} onOpenChange={setIsPromoteModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider">
              Use This in Write
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Where do you want this to land?
            </p>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Content Preview */}
            <div className="p-3 bg-muted/30 rounded-sm max-h-32 overflow-y-auto">
              <p className="font-mono text-sm whitespace-pre-wrap">{promoteContent}</p>
            </div>

            {/* Title (optional) */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Title (optional)
              </label>
              <Input
                value={promoteTitle}
                onChange={(e) => setPromoteTitle(e.target.value)}
                placeholder="Give it a name..."
                data-testid="promote-title-input"
              />
            </div>

            {/* Action Selection */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-muted/30 transition-colors">
                <input
                  type="radio"
                  name="promoteAction"
                  value="new_bit"
                  checked={promoteAction === "new_bit"}
                  onChange={() => setPromoteAction("new_bit")}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-sm">Start a New Bit</span>
                  <p className="text-xs text-muted-foreground">
                    Create a fresh bit draft from this.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-muted/30 transition-colors">
                <input
                  type="radio"
                  name="promoteAction"
                  value="add_to_bit"
                  checked={promoteAction === "add_to_bit"}
                  onChange={() => setPromoteAction("add_to_bit")}
                  className="mt-1"
                />
                <div className="flex-1">
                  <span className="font-medium text-sm">Add to an Existing Bit</span>
                  <p className="text-xs text-muted-foreground mb-2">
                    Drop this into a bit you&apos;re already building.
                  </p>
                  {promoteAction === "add_to_bit" && (
                    <Select value={targetBitId} onValueChange={setTargetBitId}>
                      <SelectTrigger className="w-full" data-testid="target-bit-select">
                        <SelectValue placeholder="Select a bit..." />
                      </SelectTrigger>
                      <SelectContent>
                        {bits?.map((bit) => (
                          <SelectItem key={bit.bit_id} value={bit.bit_id}>
                            {bit.title || "Untitled"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-muted/30 transition-colors">
                <input
                  type="radio"
                  name="promoteAction"
                  value="new_bit_with_section"
                  checked={promoteAction === "new_bit_with_section"}
                  onChange={() => setPromoteAction("new_bit_with_section")}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-sm">
                    Start a New Bit (Same Topic) + Add as a Section
                  </span>
                  <p className="text-xs text-muted-foreground">
                    Keep everything grouped under one bigger bit.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPromoteModalOpen(false)}>
              Stay Here
            </Button>
            <Button
              onClick={handlePromote}
              disabled={
                !promoteContent.trim() ||
                (promoteAction === "add_to_bit" && !targetBitId)
              }
              className="bg-primary text-primary-foreground"
              data-testid="promote-submit"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to Write
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
